import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pathFor, type Lang } from '../../router/paths';

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === 'en' ? 'en' : 'de';
  const homePath = pathFor('home', lang);

  const navigateToSection = (path: string, id: string) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'instant' });
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
    }, 300);
  };

  const scrollToSection = (id: string) => {
    if (location.pathname !== homePath) {
      navigate(homePath);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
      }, 300);
    } else {
      const el = document.getElementById(id);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
    }
  };

  const go = (path: string) => { navigate(path); window.scrollTo({ top: 0, behavior: 'instant' }); };

  // Cmd/Strg/Shift-Klick durchlassen, damit "in neuem Tab oeffnen" funktioniert.
  const isPlainClick = (e: React.MouseEvent) => !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey);

  // Jeder Eintrag hat ein echtes Ziel (href) - Suchmaschinen koennen ihm folgen.
  const navItems: { label: string; href: string; action: () => void }[] = [
    { label: t('footerHomeLink'),   href: homePath,                            action: () => go(homePath) },
    { label: t('navRooms'),         href: pathFor('rooms', lang),              action: () => go(pathFor('rooms', lang)) },
    { label: t('navHotel'),         href: pathFor('hotel', lang),              action: () => go(pathFor('hotel', lang)) },
    { label: t('navLocation'),      href: `${homePath}#lage`,                  action: () => scrollToSection('lage') },
    { label: t('navContact'),       href: `${homePath}#kontakt`,               action: () => scrollToSection('kontakt') },
    { label: t('navAusflugsziele'), href: pathFor('ausflugsziele', lang),      action: () => go(pathFor('ausflugsziele', lang)) },
    { label: t('footerCareerLink'), href: `${pathFor('hotel', lang)}#karriere`, action: () => navigateToSection(pathFor('hotel', lang), 'karriere') },
  ];

  const legalLinks = [
    { label: t('footerLegalImpressum'),   path: pathFor('impressum', lang) },
    { label: t('footerLegalDatenschutz'), path: pathFor('datenschutz', lang) },
    { label: t('footerLegalAgb'),         path: pathFor('agb', lang) },
  ];

  return (
    <footer className="bg-amber-700 text-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center">
        <Link to={homePath} onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })} className="inline-block">
          <img
            src="/images/brand/logo_hotel_alpenhof.png"
            alt="Hotel Alpenhof Gauting Logo"
            className="h-24 sm:h-32 w-auto mx-auto mb-6"
          />
        </Link>

        <h2 className="text-xl sm:text-2xl font-serif italic leading-snug max-w-xl mx-auto mb-12">
          {t('footerTagline')}
        </h2>

        <div className="w-12 h-px bg-white/40 mx-auto mb-10" />

        <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-12 text-sm">
          {navItems.map(({ label, href, action }) => (
            <Link
              key={label}
              to={href}
              onClick={(e) => { if (!isPlainClick(e)) return; e.preventDefault(); action(); }}
              className="hover:underline cursor-pointer"
            >
              {label}
            </Link>
          ))}
        </nav>

        <div className="w-12 h-px bg-white/40 mx-auto mb-10" />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-10 mb-10 text-sm sm:text-base">
          <div>
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-white/80 mb-3">{t('footerSectionContact')}</p>
            <p className="whitespace-pre-line leading-relaxed mb-3">{t('footerAddress')}</p>
            <p className="leading-relaxed">
              <a href="tel:+498985642910" className="hover:underline block">{t('footerPhone')}</a>
              <a href="mailto:hotel@alpenhof-gauting.de" className="hover:underline block">hotel@alpenhof-gauting.de</a>
            </p>
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-white/80 mb-3">{t('footerSectionReception')}</p>
            <p className="leading-relaxed">
              <span className="block">{t('footerReceptionWeekday')}</span>
              <span className="block">{t('footerReceptionWeekend')}</span>
            </p>
          </div>
        </div>

        <Link
          to={`${homePath}#kontakt`}
          onClick={(e) => { if (!isPlainClick(e)) return; e.preventDefault(); scrollToSection('kontakt'); }}
          className="inline-flex items-center justify-center gap-2 border border-white/80 hover:bg-white hover:text-amber-700 text-white text-sm font-medium px-6 py-2.5 rounded-md transition-colors duration-200 cursor-pointer"
        >
          <i className="ri-calendar-check-line text-base" />
          {t('footerCtaButton')}
        </Link>

        <div className="mt-14 pt-6 border-t border-white/25 text-xs space-y-3">
          <p>{t('footerCopyright')}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
            {legalLinks.map((item, i, arr) => (
              <span key={item.label} className="flex items-center gap-3">
                <Link
                  to={item.path}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                  className="hover:underline cursor-pointer"
                >
                  {item.label}
                </Link>
                {i < arr.length - 1 && <span className="opacity-60">·</span>}
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
